import { alpha } from '@mui/material/styles';

/**
 * Border-radius scale (px) — Meridian: squared, precise controls.
 * xs — chips, nav items, tooltips
 * sm — buttons, inputs
 * md — cards, floating panels (navbar, sidebar, footer)
 */
export const RADIUS = {
  xs: 6,
  sm: 8,
  md: 10,
} as const;

/**
 * Height (px) of one navigation target — a sidebar row, and a tab in the
 * secondary strip one level down. Shared so the two cannot drift; pinned by
 * `Interface/tests/layout.spec.ts`.
 */
export const NAV_ITEM_HEIGHT = 44;

/**
 * MLP Interface Design System — "Meridian" edition
 *
 * Source of record: the claude.ai/design project "MLP Interface Design System"
 * (templates/meridian + templates/meridian-primitives). Meridian is the chosen
 * JLAM direction: structured & precise — teal-blue primary, hairline borders,
 * tabular numbers, squared controls. A calm control centre.
 *
 * Single source of truth for all brand/semantic colors, radii, shadows, and
 * state washes. Components never define their own colors: they consume the
 * theme's semantic palette (primary/secondary/success/error/warning/info,
 * text.*, background.*, divider) or the tokens exported here. This is the
 * Meridian profile; a colour profile replaces its six roles and what derives
 * from them while the app runs (`colourRoles.ts`), so a component that needs
 * one of those reads it from the theme, never from here.
 *
 * Palette roles (Meridian token names in parentheses):
 *  primary         Meridian teal (meridian-600/700/500) — CTAs, nav accents, focus
 *  secondary       Grove green (grove-600) — supporting accent
 *  button          Contained CTA colors: primary teal, secondary amber (amber-500)
 *  background      App background (surface-app) and raised surfaces (paper)
 *  neutral         Utility ink (ink-500) — cancel, filters
 *  success         In range (#3F8F5F family)
 *  warning         Off track (#D68A2E family)
 *  error           Alert / destructive (#C9524A family)
 *  info            Informational teal (meridian-500/700)
 *  link            Inline hyperlinks (meridian-600)
 *  divider         Hairline borders rgba(32,60,72,0.14)
 */
export const PALETTE = {
  // Semantic color objects (extend directly into theme.palette)
  primary:    { main: '#2F6D82', light: '#3D8299', dark: '#235767', contrastText: '#FFFFFF' },
  secondary:  { main: '#3B6B4A', contrastText: '#FFFFFF' },
  neutral:  { main: '#52646C', contrastText: '#FFFFFF' },
  button: {
    primary:   { main: '#2F6D82', contrastText: '#FFFFFF' },
    secondary: { main: '#E0A341', contrastText: '#5A3D0E' },
  },

  success:    { main: '#3F8F5F', dark: '#2C6845', contrastText: '#FFFFFF' },
  error:      { main: '#C9524A', dark: '#8F342D', contrastText: '#FFFFFF' },
  warning:    { main: '#D68A2E', dark: '#8A5711', contrastText: '#FFFFFF' },
  info:       { main: '#3D8299', dark: '#235767', light: '#7FBFD4', contrastText: '#FFFFFF' },
  background: { default: '#E9F0F2', paper: '#FFFFFF' },
  text:       { primary: '#14232A', secondary: '#52646C', disabled: '#AAB8BD' },
  divider:    'rgba(32,60,72,0.14)',
  link:       '#2F6D82',

  // Structural UI surface tokens
  surface: {
    header: '#F2F7F8',        // surface-sunken — DataGrid/card headers
    toolbar: '#DCEAEE',       // management grid toolbar; its header, footer and hover are mixed from it
  },

  // Document status chip colors (Meridian wash + ink pairs)
  documentStatus: {
    uploaded:       { bg: '#DCEAEE', text: '#235767' },
    inProgress:     { bg: '#F7ECD6', text: '#8A5711' },
    readyForReview: { bg: '#EBDFE9', text: '#7C3B6B' },
    committed:      { bg: '#E3F1E9', text: '#2C6845' },
    failed:         { bg: '#F7E2E0', text: '#8F342D' },
  },

  // Where an observation is stored: provenance, not status, so these avoid the
  // success/warning readings above.
  observationSource: {
    local:        { bg: '#EDF1F2', text: '#4A5B61' },
    remoteLive:   { bg: '#DCEAEE', text: '#235767' },
    remoteCached: { bg: '#E7E4EF', text: '#4C4370' },
  },

  // Document type chip colors, matched to the reference design's per-type
  // scheme (WCAG contrast checked: 5.2–6.3:1):
  //   bloodTestReport  — same as its "PDF" chip (coral/red)
  //   stoolTestReport  — same as its blue "Report" chip
  //   consultationNote — same as its "Note" chip (amber)
  documentType: {
    bloodTestReport:   { bg: '#F7E2E0', text: '#8F342D' },
    stoolTestReport:   { bg: '#DCE9F7', text: '#1F5C96' },
    consultationNote:  { bg: '#F7ECD6', text: '#8A5711' },
    other:             { bg: '#EDF1F2', text: '#4A5B61' },
  },

  // AI/feature gradient colors (ExtractionReview heading gradient)
  gradient: {
    dark:      '#14324F',   // ocean deep
    blue:      '#2F6D82',   // meridian teal
    tealDeep:  '#1F5566',   // meridian-800
  },

  // Chart series colors — Meridian "Full spectrum" scheme, deterministic order
  chart: ['#2F6D82', '#3D8299', '#4A9E8F', '#57A778', '#E0A341', '#DD8B3A', '#C9524A', '#9C4A86'],
} as const;

/** Ordered list of chart series colors, derived from PALETTE. */
export const CHART_COLORS: readonly string[] = PALETTE.chart;

/** Soft ambient shadow for paper-like surfaces (also used by MuiPaper elevation1). */
export const SHADOWS = {
  surface: `0 2px 8px ${alpha(PALETTE.text.primary, 0.08)}`,
} as const;

/** Translucent scrim shown over content while it reloads. */
export const SCRIM = {
  loading: alpha(PALETTE.background.paper, 0.7),
} as const;

/**
 * Row/cell state washes for data grids and editable tables.
 * Derived from the semantic palette so a palette swap re-tints them.
 */
export const STATE_WASH = {
  error:   alpha(PALETTE.error.main, 0.06),
  success: alpha(PALETTE.success.main, 0.08),
  warning: alpha(PALETTE.warning.main, 0.08),
  hover:   alpha(PALETTE.info.main, 0.08),
  zebra:   alpha(PALETTE.text.primary, 0.02),
} as const;
